
import React, { useState, useEffect } from "react";
import { AppSettings, Warehouse } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../components/providers/LocalizationProvider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DollarSign,
  Languages,
  Workflow,
  Plus,
  Trash2,
  GripVertical,
  Save,
  BookOpen,
  Palette,
  Sparkles,
  Package,
  Star,
  Heart,
  Truck,
  Award,
  Shield,
  Zap,
  Smile,
  ThumbsUp,
  Feather,
  Globe,
  Mail,
  Settings as SettingsIcon,
  Store,
  CreditCard,
  Users,
  Crown,
  BarChart3,
  Send
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import PromoGroupsSettings from '../components/settings/PromoGroupsSettings';
import PaymentTypesSettings from '../components/settings/PaymentTypesSettings';
import UserRegistry from '../components/settings/UserRegistry';
import RightsRegistry from '../components/settings/RightsRegistry';
import RolesRegistry from '../components/settings/RolesRegistry';
import RoleModelReportComponent from '../components/settings/RoleModelReport';
import TargetMapSettings from '../components/settings/TargetMapSettings';
import ConsentDocumentsSettings from '../components/settings/ConsentDocumentsSettings';
import ConsentRegistrySettings from '../components/settings/ConsentRegistrySettings';
import { formatAddress } from '@/utils';

const currencies = [
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
];

const colorOptions = [
  { key: 'blue', label: 'Синий', class: 'bg-blue-500' },
  { key: 'green', label: 'Зелёный', class: 'bg-green-500' },
  { key: 'purple', label: 'Фиолетовый', class: 'bg-purple-500' },
  { key: 'orange', label: 'Оранжевый', class: 'bg-orange-500' },
  { key: 'indigo', label: 'Индиго', class: 'bg-indigo-500' },
  { key: 'emerald', label: 'Изумрудный', class: 'bg-emerald-500' },
];

const fontOptions = [
  { key: 'inter', label: 'Inter (современный)', class: 'font-inter' },
  { key: 'roboto', label: 'Roboto (классический)', class: 'font-roboto' },
  { key: 'open-sans', label: 'Open Sans (дружелюбный)', class: 'font-open-sans' },
  { key: 'lato', label: 'Lato (элегантный)', class: 'font-lato' },
  { key: 'poppins', label: 'Poppins (игривый)', class: 'font-poppins' },
  { key: 'merriweather', label: 'Merriweather (для чтения)', class: 'font-merriweather' }
];

const availableIcons = [
  { key: 'Package', label: 'Коробка', component: Package },
  { key: 'Star', label: 'Звезда', component: Star },
  { key: 'Heart', label: 'Сердце', component: Heart },
  { key: 'Truck', label: 'Грузовик', component: Truck },
  { key: 'BookOpen', label: 'Книга', component: BookOpen },
  { key: 'Award', label: 'Награда', component: Award },
  { key: 'Shield', label: 'Щит', component: Shield },
  { key: 'Zap', label: 'Молния', component: Zap },
  { key: 'Smile', label: 'Смайлик', component: Smile },
  { key: 'ThumbsUp', label: 'Лайк', component: ThumbsUp },
  { key: 'Feather', label: 'Перо', component: Feather },
  { key: 'Globe', label: 'Глобус', component: Globe },
];

const ColorPickerInput = ({ label, value, onChange }) => (
    <div>
        <Label className="text-sm font-medium text-slate-700">{label}</Label>
        <div className="flex items-center gap-2 p-2 border border-slate-300 rounded-md mt-1">
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-8 h-8 p-0 border-none cursor-pointer bg-transparent"
            />
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="border-none focus-visible:ring-0"
            />
        </div>
    </div>
);

const StorePreview = ({ settings }) => {
    if (!settings) return null;

    const primaryColor = settings.primary_color || '#f59e0b';
    const gradientFrom = settings.gradient_color_from || '#fffbeb';
    const gradientTo = settings.gradient_color_to || '#fef3c7';
    const buttonBg = settings.button_background || '#f59e0b';
    const buttonTextColor = settings.button_text_color || '#ffffff';
    const buttonHoverBg = settings.button_hover_background || '#d97706';
    const fontFamily = settings.font_family || 'inter';

    // Map font family to Tailwind class
    const fontClassMap = {
        'inter': 'font-inter',
        'roboto': 'font-roboto',
        'open-sans': 'font-open-sans',
        'lato': 'font-lato',
        'poppins': 'font-poppins',
        'merriweather': 'font-merriweather'
    };

    const selectedFontClass = fontClassMap[fontFamily] || 'font-inter';

    const iconMap = availableIcons.reduce((acc, icon) => {
        acc[icon.key] = icon.component;
        return acc;
    }, {});

    return (
        <Card className="elegant-card">
            <CardHeader><CardTitle>Предпросмотр</CardTitle></CardHeader>
            <CardContent>
                <div
                    className={`p-6 rounded-lg border ${selectedFontClass}`}
                    style={{ background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`}}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">{settings.store_title || 'Название магазина'}</h3>
                            <p className="text-sm text-slate-600">{settings.store_tagline || 'Слоган магазина'}</p>
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{settings.hero_title || 'Заголовок на главной'}</h2>
                    {(() => {
                      const raw = settings.hero_description || 'Описание на главной...';
                      return (
                        <div className="space-y-2">
                          <p className="text-slate-600 whitespace-pre-wrap text-sm">{raw}</p>
                          <p className="text-xs text-slate-500">Форматирование блоков (light/dark, цвета, ссылки) применяется на витрине.</p>
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-center gap-6 text-sm text-slate-500 flex-wrap mt-6">
                        {(settings.hero_features || []).map((feature, index) => {
                          const IconComponent = iconMap[feature.icon];
                          return (
                            <div key={index} className="flex items-center gap-2">
                              {IconComponent ? <IconComponent className="w-4 h-4" /> : null}
                              <span>{feature.title}</span>
                            </div>
                          );
                        })}
                    </div>
                    <div className="mt-8 flex justify-center items-center gap-4">
                        <button
                            className="px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            style={{
                                backgroundColor: buttonBg,
                                color: buttonTextColor,
                            }}
                        >
                            Пример кнопки
                        </button>
                        <button
                            className="px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            style={{
                                backgroundColor: buttonHoverBg,
                                color: buttonTextColor,
                            }}
                        >
                            Пример кнопки (наведение)
                        </button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default function Settings() {
  const { t } = useTranslation();
  const { changeLanguage } = useLocalization();
  // Single state object for all form data
  const [formState, setFormState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTelegramTest, setIsSendingTelegramTest] = useState(false);
  const [allWarehouses, setAllWarehouses] = useState([]);

  const getDefaultStatuses = () => [
    {name: "Заказан", color: "#3b82f6", order: 1},
    {name: "Оплачен", color: "#10b981", order: 2},
    {name: "Подписан", color: "#f59e0b", order: 3},
    {name: "Упакован", color: "#8b5cf6", order: 4},
    {name: "Отправлен", color: "#06b6d4", order: 5},
    {name: "Доставлен", color: "#059669", order: 6}
  ];



  const getDefaultSmtpSettings = () => ({
    enabled: false,
    host: '',
    port: 587,
    user: '',
    password: '',
    from_email: '',
    secure: false,
  });

  const getDefaultStoreSettings = () => ({
    store_title: "BookForge Store",
    store_tagline: "Качественные книги для души",
    hero_title: "Откройте новые миры через книги",
    hero_description: "Качественная литература от независимого издательства. Каждая книга - это путешествие, которое изменит вас.",
    primary_color: "#f59e0b",
    gradient_color_from: "#fffbeb",
    gradient_color_to: "#fef3c7",
    font_family: "inter",
    footer_text: "Создаем и издаем качественную литературу для думающих читателей",
    footer_copyright: "© 2024 BookForge Publishing. Все права защищены.",
    button_background: "#f59e0b",
    button_text_color: "#ffffff",
    button_hover_background: "#d97706",
    hero_features: [
        {title: "Быстрая доставка", description: "Доставляем по всей России в кратчайшие сроки", icon: "Package"},
        {title: "Высокое качество печати", description: "Только лучшие материалы и технологии", icon: "Star"},
        {title: "Тщательный отбор произведений", description: "Каждая книга проходит строгий отбор", icon: "Heart"}
    ],
    storefront_warehouse_ids: [],
    smtp_settings: getDefaultSmtpSettings(),
  });

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      let [settingsList, warehousesData] = await Promise.all([
        AppSettings.list(),
        Warehouse.list({ is_active: true })
      ]);
      let appSettings;
      setAllWarehouses(warehousesData || []);

      if (settingsList.length > 0) {
        appSettings = settingsList[0];
        const defaultStoreSettings = getDefaultStoreSettings();
        const existingStoreSettings = appSettings.store_settings || {};
        appSettings.store_settings = {
          ...defaultStoreSettings,
          ...existingStoreSettings,
          telegram_bot_settings: {
            enabled: false,
            bot_token: '',
            chat_id: '',
            events: {
              order_created: false,
              order_paid: false,
              waitlist_added: false
            },
            ...(existingStoreSettings.telegram_bot_settings || {})
          },
          smtp_settings: {
            ...getDefaultSmtpSettings(),
            ...(existingStoreSettings.smtp_settings || {})
          }
        };
         appSettings.order_statuses = appSettings.order_statuses && appSettings.order_statuses.length > 0 ? appSettings.order_statuses : getDefaultStatuses();

        if (appSettings.payment_timeout_minutes === undefined || appSettings.payment_timeout_minutes === null) {
            appSettings.payment_timeout_minutes = 60;
        }
      } else {
        appSettings = await AppSettings.create({
          language: 'ru',
          currency: 'RUB',
          currency_symbol: '₽',
          order_statuses: getDefaultStatuses(),
          store_settings: getDefaultStoreSettings(),
          payment_timeout_minutes: 60,
        });
      }
      setFormState(appSettings);
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Generic handler for top-level settings
  const handleSettingsChange = (field, value) => {
    setFormState(prev => ({...prev, [field]: value}));
  };

  // Handler for nested store_settings
  const handleStoreSettingsChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      store_settings: {
        ...prev.store_settings,
        [field]: value
      }
    }));
  };

  const handleSmtpSettingsChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      store_settings: {
        ...prev.store_settings,
        smtp_settings: {
          ...(prev.store_settings?.smtp_settings || {}),
          [field]: value
        }
      }
    }));
  };
  
  const handleWarehouseSelectionChange = (warehouseId, isChecked) => {
    setFormState(prev => {
        const currentIds = prev.store_settings.storefront_warehouse_ids || [];
        const newIds = isChecked ? [...currentIds, warehouseId] : currentIds.filter(id => id !== warehouseId);
        return {
          ...prev,
          store_settings: { ...prev.store_settings, storefront_warehouse_ids: newIds }
        };
    });
  };

  const handleCurrencyChange = (currencyCode) => {
    const currencyData = currencies.find(c => c.code === currencyCode);
    setFormState(prev => ({
      ...prev,
      currency: currencyCode,
      currency_symbol: currencyData.symbol
    }));
  };

  const handleLanguageChange = async (lang) => {
    handleSettingsChange('language', lang);
    await changeLanguage(lang);
  };
  
  // Handlers for arrays of objects (statuses, payments)
  const createArrayUpdateHandler = (arrayName) => {
      return (index, field, value) => {
          setFormState(prev => {
              const newArray = [...prev[arrayName]];
              newArray[index] = { ...newArray[index], [field]: value };
              return { ...prev, [arrayName]: newArray };
          });
      };
  };
  const updateStatus = createArrayUpdateHandler('order_statuses');
  
  // Specific handler for nested hero_features within store_settings
  const handleFeatureChange = (index, field, value) => {
      setFormState(prev => {
        const newArray = [...prev.store_settings.hero_features];
        newArray[index] = { ...newArray[index], [field]: value };
        return { ...prev, store_settings: { ...prev.store_settings, hero_features: newArray } };
      });
  };
  
  const handleStatusDragEnd = (result) => {
    if (!result.destination) return;
    const newStatuses = Array.from(formState.order_statuses);
    const [reorderedStatus] = newStatuses.splice(result.source.index, 1);
    newStatuses.splice(result.destination.index, 0, reorderedStatus);
    const updatedStatuses = newStatuses.map((status, index) => ({...status, order: index + 1}));
    setFormState(prev => ({ ...prev, order_statuses: updatedStatuses }));
  };

  const addNewStatus = () => {
    const newStatus = { name: "Новый статус", color: "#6b7280", order: formState.order_statuses.length + 1 };
    setFormState(prev => ({ ...prev, order_statuses: [...prev.order_statuses, newStatus] }));
  };
  
  const removeStatus = (index) => {
    const newStatuses = formState.order_statuses.filter((_, i) => i !== index).map((status, i) => ({ ...status, order: i + 1 }));
    setFormState(prev => ({ ...prev, order_statuses: newStatuses }));
  };
  

  
  const addFeature = () => {
    const newFeature = { title: 'Новое преимущество', description: 'Описание преимущества', icon: 'Smile' };
    setFormState(prev => ({ ...prev, store_settings: { ...prev.store_settings, hero_features: [...prev.store_settings.hero_features, newFeature] } }));
  };
  
  const removeFeature = (index) => {
    const newFeatures = formState.store_settings.hero_features.filter((_, i) => i !== index);
    setFormState(prev => ({ ...prev, store_settings: { ...prev.store_settings, hero_features: newFeatures } }));
  };
  
  const handleFeatureDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(formState.store_settings.hero_features);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFormState(prev => ({ ...prev, store_settings: { ...prev.store_settings, hero_features: items } }));
  };

  const saveSettings = async () => {
    if (!formState?.id) {
        alert("Ошибка: не удалось определить ID настроек.");
        return;
    }
    setIsSaving(true);
    try {
      // The formState is always up-to-date, so we can save it directly.
      await AppSettings.update(formState.id, formState);
      await loadSettings(); // Reload from DB to confirm changes
      alert(t('pages.settings.settings_saved'));
    } catch (error) {
      console.error("Error saving settings:", error);
      alert(t('pages.settings.settings_error'));
    }
    setIsSaving(false);
  };

  const sendTelegramTestMessage = async () => {
    const telegramSettings = formState.store_settings?.telegram_bot_settings || {};
    if (!telegramSettings.enabled || !telegramSettings.bot_token || !telegramSettings.chat_id) {
      alert('Укажите токен бота и ID чата, затем повторите отправку.');
      return;
    }

    setIsSendingTelegramTest(true);
    try {
      await AppSettings.update(formState.id, formState);
      await AppSettings.sendTelegramTestMessage();
      await loadSettings();
      alert('Тестовое сообщение поставлено в очередь Telegram уведомлений.');
    } catch (error) {
      console.error('Error sending Telegram test message:', error);
      alert(error.message || 'Не удалось отправить тестовое сообщение.');
    } finally {
      setIsSendingTelegramTest(false);
    }
  };

  if (isLoading || !formState) {
    return <div className="flex items-center justify-center h-screen">Загрузка настроек...</div>;
  }

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('pages.settings.title')}</h1>
            <p className="text-slate-600 font-medium mt-1">{t('pages.settings.subtitle')}</p>
          </div>
          <Button onClick={saveSettings} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6">
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? t('general.saving') : t('general.save')}
          </Button>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            {[
              { value: "general", label: t('pages.settings.language') },
              { value: "currency", label: t('pages.settings.currency') },
              { value: "payments", label: t('payments.title') },
              { value: "deliveries", label: 'Виды доставки' },
              { value: "store", label: t('store.title') },
              { value: "targetmap", label: 'Target Map' },
              { value: "promo", label: t('promo.title') },
              { value: "statuses", label: t('pages.settings.order_statuses') },
              { value: "telegram", label: "Telegram уведомления" },
              { value: "roles", label: "Роли и права" },
              { value: "consent_docs", label: "Документы и согласия" },
              { value: "consent_registry", label: "Реестр согласий" }
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="general">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages className="w-5 h-5 text-amber-600" />
                  {t('pages.settings.language_settings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">{t('pages.settings.language_desc')}</p>
                <Select value={formState?.language || 'ru'} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ru">Русский</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="sr">Српски</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="currency">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  {t('pages.settings.currency_settings')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="currency">{t('pages.settings.currency')}</Label>
                  <Select value={formState.currency || ''} onValueChange={handleCurrencyChange}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.name} ({c.symbol})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="symbol">{t('pages.settings.currency_symbol')}</Label>
                  <Input id="symbol" value={formState.currency_symbol || ''} readOnly />
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">{t('pages.settings.preview')}</p>
                  <p className="text-lg font-semibold text-slate-900">{t('pages.settings.price_example', { symbol: formState.currency_symbol })}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <PaymentTypesSettings />
          </TabsContent>

          <TabsContent value="deliveries">
            <div className="space-y-6">
              <Card className="elegant-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-600" />Виды доставки
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>СДЭК: Пункт выдачи заказов</Label>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={(formState.delivery_methods || []).some(m => m.type === 'cdek_delivery_point' && m.enabled)}
                        onCheckedChange={(checked) => {
                          setFormState(prev => {
                            const others = (prev.delivery_methods || []).filter(m => m.type !== 'cdek_delivery_point');
                            return { ...prev, delivery_methods: [...others, { type: 'cdek_delivery_point', enabled: checked }] };
                          });
                        }}
                      />
                      <span className="text-sm text-slate-600">Разрешить выбор пункта выдачи</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>СДЭК: Курьерская доставка</Label>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={(formState.delivery_methods || []).some(m => m.type === 'cdek_courier' && m.enabled)}
                        onCheckedChange={(checked) => {
                          setFormState(prev => {
                            const others = (prev.delivery_methods || []).filter(m => m.type !== 'cdek_courier');
                            return { ...prev, delivery_methods: [...others, { type: 'cdek_courier', enabled: checked }] };
                          });
                        }}
                      />
                      <span className="text-sm text-slate-600">Разрешить курьерскую доставку</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Самовывоз</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormState(prev => {
                            const current = prev.delivery_methods || [];
                            const newItem = { type: 'self_pickup', enabled: true, address: '' };
                            return { ...prev, delivery_methods: [...current, newItem] };
                          });
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />Добавить адрес
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(formState.delivery_methods || []).filter(m => m.type === 'self_pickup').map((item, idx) => (
                        <div key={idx} className="p-3 bg-white border border-slate-200 rounded-lg flex items-center gap-3">
                          <Switch
                            checked={!!item.enabled}
                            onCheckedChange={(checked) => {
                              setFormState(prev => {
                                const methods = (prev.delivery_methods || []).map((m, i) => i === (prev.delivery_methods || []).findIndex(x => x === item) ? { ...m, enabled: checked } : m);
                                return { ...prev, delivery_methods: methods };
                              });
                            }}
                          />
                          <Input
                            placeholder="Адрес самовывоза"
                            value={item.address || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormState(prev => {
                                const methods = (prev.delivery_methods || []).map((m, i) => i === (prev.delivery_methods || []).findIndex(x => x === item) ? { ...m, address: value } : m);
                                return { ...prev, delivery_methods: methods };
                              });
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setFormState(prev => {
                                const methods = (prev.delivery_methods || []).filter((m, i) => i !== (prev.delivery_methods || []).findIndex(x => x === item));
                                return { ...prev, delivery_methods: methods };
                              });
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="store">
            <div className="space-y-6">
              <Card className="elegant-card">
                <CardHeader><CardTitle className="flex items-center gap-2"><Store className="w-5 h-5 text-amber-600" />Основные настройки магазина</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label htmlFor="store_title">Название магазина</Label><Input id="store_title" value={formState.store_settings.store_title || ''} onChange={(e) => handleStoreSettingsChange('store_title', e.target.value)} /></div>
                    <div><Label htmlFor="store_tagline">Слоган</Label><Input id="store_tagline" value={formState.store_settings.store_tagline || ''} onChange={(e) => handleStoreSettingsChange('store_tagline', e.target.value)} /></div>
                  </div>
                  <div><Label htmlFor="hero_title">Заголовок на главной</Label><Input id="hero_title" value={formState.store_settings.hero_title || ''} onChange={(e) => handleStoreSettingsChange('hero_title', e.target.value)} /></div>
                  <div><Label htmlFor="hero_description">Описание на главной</Label><Textarea id="hero_description" value={formState.store_settings.hero_description || ''} onChange={(e) => handleStoreSettingsChange('hero_description', e.target.value)} className="h-24" /></div>
                  <div><Label htmlFor="footer_text">Текст в футере</Label><Textarea id="footer_text" value={formState.store_settings.footer_text || ''} onChange={(e) => handleStoreSettingsChange('footer_text', e.target.value)} className="min-h-32 resize-y" /></div>
                  <div><Label htmlFor="footer_copyright">Копирайт в футере</Label><Input id="footer_copyright" value={formState.store_settings.footer_copyright || ''} onChange={(e) => handleStoreSettingsChange('footer_copyright', e.target.value)} placeholder="© 2024 BookForge Publishing. Все права защищены." /></div>
                </CardContent>
              </Card>

              <Card className="elegant-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-amber-600" />
                    SMTP настройки
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Включить SMTP</p>
                      <p className="text-sm text-slate-500">Используется для отправки email уведомлений клиентам</p>
                    </div>
                    <Switch
                      checked={formState.store_settings.smtp_settings.enabled}
                      onCheckedChange={(checked) => handleSmtpSettingsChange('enabled', checked)}
                    />
                  </div>
                  {formState.store_settings.smtp_settings.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="smtp_host">SMTP сервер</Label>
                        <Input
                          id="smtp_host"
                          value={formState.store_settings.smtp_settings.host || ''}
                          onChange={(e) => handleSmtpSettingsChange('host', e.target.value)}
                          placeholder="mail.smtp2go.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_port">Порт</Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={formState.store_settings.smtp_settings.port ?? ''}
                          onChange={(e) => handleSmtpSettingsChange('port', e.target.value ? parseInt(e.target.value, 10) : null)}
                          placeholder="2525"
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_user">SMTP пользователь</Label>
                        <Input
                          id="smtp_user"
                          value={formState.store_settings.smtp_settings.user || ''}
                          onChange={(e) => handleSmtpSettingsChange('user', e.target.value)}
                          placeholder="bookforge"
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_password">SMTP пароль</Label>
                        <Input
                          id="smtp_password"
                          type="password"
                          value={formState.store_settings.smtp_settings.password || ''}
                          onChange={(e) => handleSmtpSettingsChange('password', e.target.value)}
                          placeholder="********"
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_from">Email отправителя</Label>
                        <Input
                          id="smtp_from"
                          value={formState.store_settings.smtp_settings.from_email || ''}
                          onChange={(e) => handleSmtpSettingsChange('from_email', e.target.value)}
                          placeholder="no-reply@yourdomain.com"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          id="smtp_secure"
                          checked={formState.store_settings.smtp_settings.secure || false}
                          onCheckedChange={(checked) => handleSmtpSettingsChange('secure', checked)}
                        />
                        <div>
                          <Label htmlFor="smtp_secure" className="font-medium">Использовать SSL/TLS</Label>
                          <p className="text-sm text-slate-500">Включите для портов 465/SSL</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="elegant-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-amber-600" />
                        Управление видимостью товаров
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-slate-600 mb-6">
                        Выберите склады, остатки на которых будут учитываться для отображения товаров в витрине. Если ни один склад не выбран, товары не будут отображаться.
                    </p>
                    <div className="grid grid-cols-1 gap-3">
                        {allWarehouses.map(warehouse => (
                            <div
                                key={warehouse.id}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    (formState.store_settings.storefront_warehouse_ids || []).includes(warehouse.id)
                                        ? 'border-amber-500 bg-amber-50'
                                        : 'border-slate-200 hover:border-slate-300'
                                }`}
                                onClick={() => handleWarehouseSelectionChange(warehouse.id, !(formState.store_settings.storefront_warehouse_ids || []).includes(warehouse.id))}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                        (formState.store_settings.storefront_warehouse_ids || []).includes(warehouse.id)
                                            ? 'border-amber-500 bg-amber-500'
                                            : 'border-slate-300'
                                    }`}>
                                        {(formState.store_settings.storefront_warehouse_ids || []).includes(warehouse.id) && (
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">{warehouse.name}</p>
                                        {warehouse.address && (
                                            <p className="text-sm text-slate-500">
                                                {formatAddress(warehouse.address)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {allWarehouses.length === 0 && (
                            <div className="text-center py-8 text-slate-500">
                                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                <p className="font-medium">Склады не настроены</p>
                                <p className="text-sm">Создайте склады в разделе "Склад" для управления остатками</p>
                            </div>
                        )}
                    </div>
                </CardContent>
              </Card>

              <Card className="elegant-card">
                <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5 text-amber-600" />Дизайн и стиль</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                  <div>
                     <ColorPickerInput
                        label="Основной цвет (кнопки, акценты)"
                        value={formState.store_settings.primary_color || ''}
                        onChange={(v) => handleStoreSettingsChange('primary_color', v)}
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">Цвета кнопок</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                       <ColorPickerInput
                            label="Фон кнопки"
                            value={formState.store_settings.button_background || '#f59e0b'}
                            onChange={(v) => handleStoreSettingsChange('button_background', v)}
                       />
                       <ColorPickerInput
                            label="Цвет текста кнопки"
                            value={formState.store_settings.button_text_color || '#ffffff'}
                            onChange={(v) => handleStoreSettingsChange('button_text_color', v)}
                       />
                       <ColorPickerInput
                            label="Фон при наведении"
                            value={formState.store_settings.button_hover_background || '#d97706'}
                            onChange={(v) => handleStoreSettingsChange('button_hover_background', v)}
                       />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700">Градиент фона</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                       <ColorPickerInput
                            label="От"
                            value={formState.store_settings.gradient_color_from || ''}
                            onChange={(v) => handleStoreSettingsChange('gradient_color_from', v)}
                       />
                       <ColorPickerInput
                            label="До"
                            value={formState.store_settings.gradient_color_to || ''}
                            onChange={(v) => handleStoreSettingsChange('gradient_color_to', v)}
                       />
                    </div>
                  </div>

                  <div>
                    <Label>Шрифт</Label>
                    <Select value={formState.store_settings.font_family || 'inter'} onValueChange={(v) => handleStoreSettingsChange('font_family', v)}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>{fontOptions.map(font => (<SelectItem key={font.key} value={font.key}><span className={font.class}>{font.label}</span></SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card className="elegant-card">
                <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-amber-600" />
                            Преимущества
                        </div>
                        <Button onClick={addFeature} variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Добавить
                        </Button>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-slate-600 mb-4">
                        Настройте список преимуществ, которые отображаются на главной странице витрины.
                    </p>
                    <DragDropContext onDragEnd={handleFeatureDragEnd}>
                        <Droppable droppableId="features">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                                    {formState.store_settings.hero_features.map((feature, index) => (
                                        <Draggable key={`feature_${index}`} draggableId={`feature_${index}`} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps} className="p-3 bg-white border border-slate-200 rounded-lg">
                                                    <div className="flex items-center gap-3">
                                                        <div {...provided.dragHandleProps} className="cursor-grab text-slate-400">
                                                            <GripVertical className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            <Input
                                                                value={feature.title}
                                                                onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                                                                placeholder="Название преимущества"
                                                            />
                                                            <Input
                                                                value={feature.description}
                                                                onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                                                                placeholder="Описание преимущества"
                                                            />
                                                            <Select value={feature.icon} onValueChange={(v) => handleFeatureChange(index, 'icon', v)}>
                                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    {availableIcons.map(icon => (
                                                                        <SelectItem key={icon.key} value={icon.key}>{icon.label}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <Button variant="ghost" size="icon" onClick={() => removeFeature(index)}>
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </CardContent>
              </Card>

              <StorePreview settings={formState.store_settings} />
            </div>
          </TabsContent>

          <TabsContent value="targetmap">
            <TargetMapSettings />
          </TabsContent>

          <TabsContent value="promo">
            <PromoGroupsSettings />
          </TabsContent>

          <TabsContent value="statuses">
            <Card className="elegant-card">
              <CardHeader>
                 <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Workflow className="w-5 h-5 text-amber-600" />{t('pages.settings.order_statuses')}</div>
                  <Button onClick={addNewStatus} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />{t('pages.settings.add_status')}</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">{t('pages.settings.statuses_desc')}</p>
                <DragDropContext onDragEnd={handleStatusDragEnd}>
                  <Droppable droppableId="statuses">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                        {formState.order_statuses.map((status, index) => (
                          <Draggable key={`status_${index}`} draggableId={`status_${index}`} index={index}>
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.draggableProps} className="p-4 bg-white border border-slate-200 rounded-lg">
                                <div className="flex items-center gap-4">
                                  <div {...provided.dragHandleProps} className="cursor-grab text-slate-400"><GripVertical className="w-5 h-5" /></div>
                                  <div className="flex-1 grid grid-cols-2 gap-4">
                                    <div><Label>{t('pages.settings.status_name')}</Label><Input value={status.name} onChange={(e) => updateStatus(index, 'name', e.target.value)} /></div>
                                    <div><Label>{t('pages.settings.status_color')}</Label><ColorPickerInput value={status.color} onChange={(v) => updateStatus(index, 'color', v)} /></div>
                                  </div>
                                  <Button variant="ghost" size="icon" onClick={() => removeStatus(index)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </CardContent>
            </Card>
            <Card className="elegant-card mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-amber-600" />
                  Автоматизация склада
                </CardTitle>
              </CardHeader>
              <CardContent>
                 <p className="text-sm text-slate-600 mb-4">
                    Настройте автоматическое создание и отмену резервов для заказов.
                </p>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="payment_timeout_minutes">Время резерва до оплаты (минут)</Label>
                        <Input
                            id="payment_timeout_minutes"
                            type="number"
                            value={formState.payment_timeout_minutes || ''}
                            onChange={(e) => handleSettingsChange('payment_timeout_minutes', parseInt(e.target.value, 10))}
                            className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            Укажите, сколько минут товар будет находиться в резерве после начала оплаты. По истечении этого времени накладная станет неактивной, а товар вернется в свободный остаток.
                        </p>
                    </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="telegram">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-amber-600" />
                  Telegram уведомления
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="telegram_enabled">Включить Telegram уведомления</Label>
                      <p className="text-sm text-slate-500 mt-1">
                        Включите для отправки уведомлений о событиях в Telegram чат
                      </p>
                    </div>
                    <Switch
                      id="telegram_enabled"
                      checked={formState.store_settings?.telegram_bot_settings?.enabled || false}
                      onCheckedChange={(checked) => {
                        setFormState(prev => ({
                          ...prev,
                          store_settings: {
                            ...prev.store_settings,
                            telegram_bot_settings: {
                              ...prev.store_settings.telegram_bot_settings,
                              enabled: checked
                            }
                          }
                        }));
                      }}
                    />
                  </div>

                  {formState.store_settings?.telegram_bot_settings?.enabled && (
                    <>
                      <div>
                        <Label htmlFor="telegram_token">Токен бота</Label>
                        <Input
                          id="telegram_token"
                          type="password"
                          placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                          value={formState.store_settings?.telegram_bot_settings?.bot_token || ''}
                          onChange={(e) => {
                            setFormState(prev => ({
                              ...prev,
                              store_settings: {
                                ...prev.store_settings,
                                telegram_bot_settings: {
                                  ...prev.store_settings.telegram_bot_settings,
                                  bot_token: e.target.value
                                }
                              }
                            }));
                          }}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Получите токен у @BotFather в Telegram
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="telegram_chat_id">ID чата</Label>
                        <Input
                          id="telegram_chat_id"
                          placeholder="-1001234567890"
                          value={formState.store_settings?.telegram_bot_settings?.chat_id || ''}
                          onChange={(e) => {
                            setFormState(prev => ({
                              ...prev,
                              store_settings: {
                                ...prev.store_settings,
                                telegram_bot_settings: {
                                  ...prev.store_settings.telegram_bot_settings,
                                  chat_id: e.target.value
                                }
                              }
                            }));
                          }}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          ID группы или канала, куда будут отправляться уведомления. Для получения ID добавьте бота в группу и используйте @userinfobot
                        </p>
                      </div>

                      <div className="flex justify-end pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={sendTelegramTestMessage}
                          disabled={isSendingTelegramTest}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {isSendingTelegramTest ? 'Отправка...' : 'Тестовое сообщение'}
                        </Button>
                      </div>

                      <div className="space-y-3 pt-4 border-t">
                        <Label>События для уведомлений</Label>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="event_order_created"
                              checked={formState.store_settings?.telegram_bot_settings?.events?.order_created || false}
                              onCheckedChange={(checked) => {
                                setFormState(prev => ({
                                  ...prev,
                                  store_settings: {
                                    ...prev.store_settings,
                                    telegram_bot_settings: {
                                      ...prev.store_settings.telegram_bot_settings,
                                      events: {
                                        ...prev.store_settings.telegram_bot_settings.events,
                                        order_created: checked
                                      }
                                    }
                                  }
                                }));
                              }}
                            />
                            <Label htmlFor="event_order_created" className="cursor-pointer">
                              Заказ подготовлен (создан)
                            </Label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="event_order_paid"
                              checked={formState.store_settings?.telegram_bot_settings?.events?.order_paid || false}
                              onCheckedChange={(checked) => {
                                setFormState(prev => ({
                                  ...prev,
                                  store_settings: {
                                    ...prev.store_settings,
                                    telegram_bot_settings: {
                                      ...prev.store_settings.telegram_bot_settings,
                                      events: {
                                        ...prev.store_settings.telegram_bot_settings.events,
                                        order_paid: checked
                                      }
                                    }
                                  }
                                }));
                              }}
                            />
                            <Label htmlFor="event_order_paid" className="cursor-pointer">
                              Заказ оплачен
                            </Label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="event_waitlist_added"
                              checked={formState.store_settings?.telegram_bot_settings?.events?.waitlist_added || false}
                              onCheckedChange={(checked) => {
                                setFormState(prev => ({
                                  ...prev,
                                  store_settings: {
                                    ...prev.store_settings,
                                    telegram_bot_settings: {
                                      ...prev.store_settings.telegram_bot_settings,
                                      events: {
                                        ...prev.store_settings.telegram_bot_settings.events,
                                        waitlist_added: checked
                                      }
                                    }
                                  }
                                }));
                              }}
                            />
                            <Label htmlFor="event_waitlist_added" className="cursor-pointer">
                              Добавлен пользователь в список ожидания
                            </Label>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles">
            <div className="space-y-6">
              <UserRegistry />
              <RightsRegistry />
              <RolesRegistry />
              <RoleModelReportComponent />
            </div>
          </TabsContent>

          <TabsContent value="consent_docs">
            <ConsentDocumentsSettings />
          </TabsContent>

          <TabsContent value="consent_registry">
            <ConsentRegistrySettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
